import React, { useRef, useState } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select } from 'antd';
import {
    PlusCircleOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo, { formatPriceIndian } from '../../../constants/functions';
import EditableFormBlock from '../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import Timeline from '../Timeline';
import Attachments from '../Attachments';
import MailsComponent from '../Mails/MailsComponent';

const { Title, Text, Link } = Typography;

export const accountConfig: MasterConfigs = {
    api: () => '/accounts',
    getFormColumns: () => [
        {
            span: 24,
            render: (form) => <Title level={5} className="font-bold pb-2">Account Information</Title>
        },
        {
            label: 'Account Owner',
            name: 'businessOwner',
            type: 'select',
            render: (text, data) => {
                return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
            },
            isCombo: true,
            span: 12,
        },
        {
            label: 'Rating',
            name: 'rating',
            type: 'select',
            span: 12,
            selectProps: {
                showSearch: true,
                allowClear: true,
                optionFilterProp: 'children',
            },
            children: constants.RATINGS.map(rating => ({ name: rating, value: rating })),
        },
        {
            label: 'Account Name',
            name: 'businessName',
            type: 'text',
            span: 12,
        },
        {
            label: 'Phone',
            name: 'phone',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const phoneRegex = /^[0-9]{10,15}$/;
                        return phoneRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid phone number'));
                    },
                },
            ],
        },
        {
            label: 'Account Site',
            name: 'businessSite',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
                        return urlRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid website URL'));
                    },
                },
            ],
        },
        {
            label: 'Fax',
            name: 'fax',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const faxRegex = /^[0-9+\-().\s]{6,20}$/;
                        return faxRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid fax number'));
                    },
                },
            ],
        },
        {
            label: 'Parent Business',
            name: 'parentBusiness',
            type: 'select',
            isCombo: true,
            render: (text) => text?.businessName || 'N/A',
            span: 12,
            selectProps: {
                showSearch: true,
                allowClear: true,
            },
        },
        {
            label: 'Website',
            name: 'website',
            type: 'text',
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        try {
                            new URL(value);
                            return Promise.resolve();
                        } catch {
                            return Promise.reject(new Error('Enter a valid website URL'));
                        }
                    },
                },
            ],
            span: 12,
        },
        {
            label: 'Account Number',
            name: 'businessNumber',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const businessNumberRegex = /^[0-9]{1,10}$/;
                        return businessNumberRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid business number'));
                    },
                },
            ],
        },
        {
            label: 'Ticker Symbol',
            name: 'tickerSymbol',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const tickerSymbolRegex = /^[A-Z]{1,5}$/;
                        return tickerSymbolRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid ticker symbol'));
                    },
                },
            ],
        },
        {
            label: 'Account Type',
            name: 'businessType',
            type: 'select',
            children: constants.ACCOUNT_TYPES.map(type => ({ name: type, value: type })),
            span: 12,
            selectProps: {
                showSearch: true,
                optionFilterProp: 'children',
                allowClear: true,
            },
        },
        {
            label: 'Ownership',
            name: 'ownerShip',
            type: 'select',
            span: 12,
            selectProps: {
                showSearch: true,
                optionFilterProp: 'children',
                allowClear: true,
            },
            children: constants.ACCOUNT_OWNERSHIP.map(ownership => ({ name: ownership, value: ownership })),
        },
        {
            label: 'Industry',
            name: 'industry',
            type: 'select',
            span: 12,
            selectProps: {
                showSearch: true,
                optionFilterProp: 'children',
                allowClear: true,
            },
            children: constants.INDUSTRIES.map(industry => ({ name: industry, value: industry })),
        },
        {
            label: 'Employee Count',
            name: 'noOfEmployees',
            type: 'number',
            span: 12,
        },
        {
            label: 'Annual Revenue',
            name: 'annualRevenue',
            type: 'text',
            inputProps: {
                prefix: 'INR ',
            },
            render: (text) => text ? `INR ${formatPriceIndian(text)}` : 'N/A',
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const revenueRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                        return revenueRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid annual revenue'));
                    },
                },
            ],
            span: 12,
        },
        {
            label: 'SIC Code',
            name: 'sicCode',
            type: 'text',
            span: 12
        },
        {
            label: 'GST Number',
            name: 'gstNumber',
            type: 'text',
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
                        return gstRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid GST number'));
                    },
                },
            ],
            span: 12
        },
        {
            label: 'LinkedIn Profile URL',
            name: 'linkedinProfileUrl',
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        try {
                            const url = new URL(value);
                            if (url.hostname.includes('linkedin.com')) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        } catch {
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        }
                    },
                },
            ],
            render: (text) => {
                if (!text) return 'N/A';
                return <Link href={text} target="_blank" rel="noopener noreferrer">{text}</Link>;
            },
        },
        {
            span: 24,
            render: (form) => <Title level={5} className="font-bold border-b pb-2">Billing Address Information</Title>
        },
        {
            label: 'Street',
            name: ['address', 'billing', 'street'],
            type: 'text',
            span: 12,
        },
        {
            label: 'City',
            name: ['address', 'billing', 'city'],
            type: 'text',
            span: 12,
        },
        {
            label: 'State',
            name: ['address', 'billing', 'state'],
            type: 'text',
            span: 12,
        },
        {
            label: 'Zip Code',
            name: ['address', 'billing', 'zip'],
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const zipRegex = /^[0-9]{4,10}$/;
                        return zipRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid zip code'));
                    },
                },
            ],
        },
        {
            label: 'Country',
            name: ['address', 'billing', 'country'],
            type: 'text',
            span: 12,
        },
        {
            span: 24,
            render: (form) => <Title level={5} className="font-bold border-b pb-2">Shipping Address Information</Title>
        },
        {
            label: 'Street',
            name: ['address', 'shipping', 'street'],
            type: 'text',
            span: 12,
        },
        {
            label: 'City',
            name: ['address', 'shipping', 'city'],
            type: 'text',
            span: 12,
        },
        {
            label: 'State',
            name: ['address', 'shipping', 'state'],
            type: 'text',
            span: 12,
        },
        {
            label: 'Zip Code',
            name: ['address', 'shipping', 'zip'],
            type: 'text',
            span: 12,
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const zipRegex = /^[0-9]{4,10}$/;
                        return zipRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid zip code'));
                    },
                },
            ],
        },
        {
            label: 'Country',
            name: ['address', 'shipping', 'country'],
            type: 'text',
            span: 12,
        },
        {
            span: 24,
            render: (form) => <Title level={5} className="font-bold border-b pb-2">Account Billing Information</Title>
        },
        {
            label: 'Billing To Email',
            name: 'billingToEmail',
            type: 'text',
            span: 12,
            inputProps: {
                placeholder: 'Enter billing to email address',
            },
            rules: [
                {
                    type: 'email',
                    message: 'Please enter a valid email address',
                },
            ],
        },
        {
            label: 'Billing CC Email',
            name: 'billingCcEmail',
            type: 'text',
            span: 12,
            inputProps: {
                placeholder: 'Enter CC email addresses (comma separated)',
            },
            rules: [
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const emails = value.split(',').map((email: string) => email.trim()).filter(Boolean);
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        const allValid = emails.every((email: string) => emailRegex.test(email));
                        return allValid
                            ? Promise.resolve()
                            : Promise.reject(new Error('Please enter valid email addresses'));
                    },
                },
            ],
        },
        {
            span: 24,
            render: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
        },
        {
            label: 'Description',
            name: 'description',
            type: 'textarea',
            span: 24,
            textAreaProps: {
                autoSize: { minRows: 3, maxRows: 6 },
            }
        }
    ],
    combo: [
        {
            api: '/users',
            name: 'businessOwner',
            idKey: '_id',
            render: (text) => <ProfileCard
                size={30}
                name={constants.GET_FULL_NAME(text)}
                avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                role={null}
            />
        },
        {
            api: '/accounts',
            name: 'parentBusiness',
            idKey: '_id',
            render: (text) => text?.businessName || 'N/A',
        }
    ],
}

const AccountDetails: React.FC<any> = ({ account }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();

    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    const accountOwnerConfig: MasterConfigs = {
        api: () => '/accounts',
        getFormColumns: () => [
            {
                label: 'Account Owner',
                name: 'businessOwner',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                render: (text, data) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                },
                span: 24,
            },
            {
                label: 'Industry',
                name: 'industry',
                type: 'select',
                span: 24,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.INDUSTRIES.map(industry => ({ name: industry, value: industry })),
            },
            {
                label: 'Employee Count',
                name: 'noOfEmployees',
                type: 'number',
                span: 24,
            },
            {
                label: 'Annual Revenue',
                name: 'annualRevenue',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const revenueRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return revenueRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid annual revenue'));
                        },
                    },
                ],
                render: (text) => text ? `INR ${formatPriceIndian(text)}` : 'N/A',
                span: 24,
            },
            {
                label: 'Phone',
                name: 'phone',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const phoneRegex = /^[0-9]{10,15}$/;
                            return phoneRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid phone number'));
                        },
                    },
                ],
            },
        ],
        combo: []
    }

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails', notShow: true },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];


    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(account?.updatedAt)}</Text>
                        </div>
                    </div>

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={accountOwnerConfig}
                                        id={account?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={accountConfig}
                                        id={account?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        {sections.map(section => (
                            !section?.notShow && <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'Businesses'} refId={account?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'Businesses'} refId={account?._id} /> </div>}
                            </div>
                        ))}
                        <MailsComponent Email={account?.email} sectionRefs={sectionRefs} highlighted={highlighted} />
                    </div>)}

                    {activeTab === 'timeline' && <Timeline type="Businesses" id={account?._id} />}

                </div>
            </div>
        </div>
    );
};

export default AccountDetails;